import React from "react"
import PropTypes from "prop-types"
import { Field } from "redux-form/immutable"

import classNames from "classnames"

import PhoneInput from "react-phone-input-2"
// multiple default styles are available
// list of those and other customization options:
// https://github.com/bl00mber/react-phone-input-2
import "react-phone-input-2/lib/bootstrap.css"

import ReduxFormFieldWrapper from "app/static/frontend/forms/components/ReduxFormFieldWrapper"
import regexes from "shared/imports/regex"
import { WithIcon } from "QuorumGrassroots/widgets/ReusableComponents/WithIcon"
import { PhoneInputField } from "./accessible-phone-number"
import isFeatureEnabled from "shared/featureflags/helperFunctions"

const accessibleRegistrationFF = isFeatureEnabled("ff_ngg_accessible_registration_form")

export const cleanPhoneNumber = (phoneNumber) => {
    // If it's just a country code ("+" followed by 1-3 chars), don't try to validate and submit that later.
    if (phoneNumber && phoneNumber.length <= 4) {
        return ""
    }
    return phoneNumber.replace(regexes.phoneNumberReplacerRegex, "")
}

export const renderPhoneInput = ({
    input,
    meta,
    country,
    countryCodeEditable,

    accessibilityId,
    dataCy,
    displayErrorWithoutTouch,
    label,
    labelStyle,
    style,
    tooltipText,
    showAsterisk,
    isCampaignPage,
    disabled,
    IconComponent,
    ...props
}) => {
    const formWrapperClassName = classNames("form-input")
    const phoneInputClassName = classNames(isCampaignPage ? "ngg-input-field" : "input-field", {
        "ngg-input-error-outline": meta.error && (displayErrorWithoutTouch || meta.touched) && isCampaignPage,
    })
    const withAccessibleField = accessibleRegistrationFF

    return (
        <ReduxFormFieldWrapper
            className={classNames("input", formWrapperClassName)}
            dataCy={dataCy}
            displayErrorWithoutTouch={displayErrorWithoutTouch}
            error={meta.error}
            label={label}
            labelStyle={labelStyle}
            style={style}
            tooltipText={tooltipText}
            touched={meta.touched}
            warning={meta.warning}
            accessibilityId={accessibilityId}
            showAsterisk={showAsterisk}
        >
            <WithIcon IconComponent={IconComponent}>
                {withAccessibleField ? (
                    <PhoneInputField
                        onChange={(value) => input.onChange(cleanPhoneNumber(value))}
                        inputClass={phoneInputClassName}
                        country={country}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        value={input.value}
                        disabled={disabled}
                        inputStyle={{
                            width: "100%",
                            backgroundColor: disabled && "#ecf0f1",
                        }}
                        required={props.required}
                        dataCy={dataCy}
                    />
                ) : (
                    <PhoneInput
                        country={country}
                        countryCodeEditable={countryCodeEditable}
                        onBlur={input.onBlur}
                        onFocus={input.onFocus}
                        value={input.value}
                        disabled={disabled}
                        // these settings are needed for the style to play nice in grassroots
                        inputClass={phoneInputClassName}
                        inputProps={{
                            "data-cy": dataCy,
                            "aria-required": props["aria-required"],
                        }}
                        inputStyle={{
                            width: "100%",
                            backgroundColor: disabled && "#ecf0f1",
                        }}
                        // prevent the creation of the special-label element
                        specialLabel={null}
                        isCampaignPage={isCampaignPage}
                    />
                )}
            </WithIcon>
        </ReduxFormFieldWrapper>
    )
}

export const PhoneField = ({
    accessibilityId,
    countryCodeEditable,
    dataCy,
    defaultCountry,
    disabled,
    displayErrorWithoutTouch,
    label,
    labelStyle,
    name,
    shouldCleanInput,
    style,
    tooltipText,
    validate,
    warn,
    showAsterisk,
    isCampaignPage,
    IconComponent,
    shouldShowIconWithinInput,
    ...props
}) => (
    <Field
        component={renderPhoneInput}
        field={name}
        name={name}
        accessibilityId={accessibilityId}
        dataCy={dataCy}
        disabled={disabled}
        displayErrorWithoutTouch={displayErrorWithoutTouch}
        label={label}
        labelStyle={labelStyle}
        style={style}
        tooltipText={tooltipText}
        validate={validate}
        warn={warn}
        country={defaultCountry}
        countryCodeEditable={countryCodeEditable}
        normalize={shouldCleanInput && !accessibleRegistrationFF ? cleanPhoneNumber : undefined}
        showAsterisk={showAsterisk}
        isCampaignPage={isCampaignPage}
        IconComponent={IconComponent}
        shouldShowIconWithinInput={shouldShowIconWithinInput}
        aria-required={showAsterisk}
        required={showAsterisk}
        {...props}
    />
)

PhoneField.propTypes = {
    // whether or not the country code ("+1") can be changed by typing, or
    // is only changeable by using the flag dropdown
    countryCodeEditable: PropTypes.bool,
    // string indicating the country that the country code should initialize as
    // can always be changed by the user; options as listed here:
    // https://github.com/bl00mber/react-phone-input-2/blob/master/src/rawCountries.js
    defaultCountry: PropTypes.string,
    // field name for redux form
    name: PropTypes.string,
    // true if the field should store numbers as just numeric digits ("18003334444")
    // or false if the field should store numbers as they appear ("+1 (800) 333-444")
    shouldCleanInput: PropTypes.bool,
}

export default PhoneField
